import { useEffect, useRef, useState } from 'react';
import { MessagesSquare } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { useLiveChat } from './use-livechat';
import { LiveChatPanel } from './LiveChatPanel';
import styles from './LiveChatWidget.module.css';

/**
 * Window event that opens the Live Chat window from anywhere in the app.
 * Used by surfaces that need an entry point when the floating bubble is hidden
 * (e.g. the Overview "Helpful resources" rail on mobile, where the bubble is
 * suppressed to avoid overlapping content).
 */
const OPEN_EVENT = 'livechat:open';

/** Open the global Live Chat window. Safe to call from any component. */
export function openLiveChat(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * Floating Live Chat launcher, mounted once globally in the app shell.
 *
 * The chat engine (useLiveChat) stays mounted whether the window is open or
 * closed, so an existing support room keeps receiving messages in the
 * background and the launcher can surface an unread count.
 */
export function LiveChatWidget() {
  const { user } = useAuth();
  const chat = useLiveChat(
    user ? { id: user.uuid, name: user.name, email: user.email } : null,
  );
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Open on the global event so callers can launch the chat while the floating
  // bubble is hidden (mobile) — the window itself carries its own close control.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Bump the unread pip when an agent message arrives while the window is closed.
  const agentCount = chat.messages.filter((m) => m.role === 'agent').length;
  const prevAgentCount = useRef(agentCount);
  useEffect(() => {
    const delta = agentCount - prevAgentCount.current;
    prevAgentCount.current = agentCount;
    if (!open && delta > 0) setUnread((u) => u + delta);
  }, [agentCount, open]);
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // AppLayout renders behind auth, but the user object may still be resolving.
  if (!user) return null;

  return (
    <div className={styles.root}>
      {open ? <LiveChatPanel chat={chat} onClose={() => setOpen(false)} /> : null}
      {/* Launcher tab — hidden while the window is open (the window carries its own close). */}
      {!open ? (
        <div className={styles.tabWrap}>
          <button
            type="button"
            className={styles.tab}
            aria-label="Open live chat"
            onClick={() => setOpen(true)}
          >
            <MessagesSquare size={18} className={styles.tabIcon} />
            <span className={styles.tabLabel}>Live Chat</span>
          </button>
          {unread > 0 ? (
            <span className={styles.unread}>{unread > 9 ? '9+' : unread}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
